import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMediaQuery } from "@mui/material";

import { Button as GradientButton } from "@ui/index";

import { Formik } from "formik";
import styled, { css, keyframes } from "styled-components";
import { object, string } from "yup";

import FormikInput from "@/components/FormikInput";
import { IError } from "@/models/inner-models";
import { newsletter } from "@/store/actions/newsletter";
import { useAppDispatchUnwrap } from "@/store/hooks";

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
`;

const Row = styled.div<{ $horizontal: boolean }>`
  display: flex;
  width: 100%;
  gap: 12px;
  flex-direction: ${({ $horizontal }) => ($horizontal ? "row" : "column")};
  align-items: ${({ $horizontal }) => ($horizontal ? "stretch" : "center")};
`;

const ShakeWrap = styled.div<{ $shake?: boolean; $horizontal?: boolean }>`
  width: 100%;
  /* horizontal: each input grows equally but never shrinks below a readable width */
  ${({ $horizontal }) =>
    $horizontal &&
    css`
      flex: 1 1 0;
      min-width: 180px;
    `}
  animation: ${({ $shake }) => ($shake ? shake : "none")} 0.5s ease;

  .MuiFormHelperText-root {
    display: none;
  }

  .MuiOutlinedInput-root.Mui-error {
    border-color: #e47a6a !important;
  }
`;

const BtnWrap = styled.div<{ $horizontal: boolean }>`
  flex-shrink: 0;
  width: ${({ $horizontal }) => ($horizontal ? "auto" : "100%")};
`;

const EnvIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const validationSchema = (t: (key: string) => string) => {
  return object({
    firstName: string()
      .matches(/^\S+$/, t("noSpaces"))
      .required(t("fieldRequired")),
    email: string().matches(emailRegex, t("emailValidation")).required(t("fieldRequired")),
  });
};

interface IForm {
  handleOpenDialog: () => void;
}

const Form: FC<IForm> = ({ handleOpenDialog }) => {
  const { t: tValidation } = useTranslation("validation");
  const [shakingFields, setShakingFields] = useState<Record<string, boolean>>({});

  // keep the form horizontal down to 640px (was the 900px desktop breakpoint)
  const isDesktop = useMediaQuery("(min-width:640px)");

  const dispatch = useAppDispatchUnwrap();

  const triggerShake = (fields: string[]) => {
    const shaking: Record<string, boolean> = {};
    fields.forEach((f) => (shaking[f] = true));
    setShakingFields(shaking);
    setTimeout(() => setShakingFields({}), 500);
  };

  const initialValues = useMemo(
    () => ({
      firstName: "",
      email: "",
    }),
    [isDesktop],
  );

  return (
    <Formik
      enableReinitialize
      // Validate ONLY on Subscribe (the button runs setTouched + validateForm).
      // Without these, blurring a field ran validation and lit the red border
      // before Subscribe was ever clicked.
      validateOnBlur={false}
      validateOnChange={false}
      initialValues={initialValues}
      validationSchema={() => validationSchema(tValidation)}
      onSubmit={async (values, { setErrors, resetForm }) => {
        try {
          await dispatch(newsletter(values));
          handleOpenDialog();
          resetForm();
        } catch (err) {
          const message = (err as IError).message;
          setErrors({ email: message });
        }
      }}
    >
      {({ handleSubmit, validateForm, setTouched }) => (
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <Row $horizontal={isDesktop}>
            <ShakeWrap
              $shake={shakingFields.firstName}
              $horizontal={isDesktop}
              onKeyDown={(e) => e.key === " " && e.preventDefault()}
            >
              <FormikInput fieldName="firstName" label="First Name" placeholder="First Name" autoComplete="name" />
            </ShakeWrap>

            <ShakeWrap $shake={shakingFields.email} $horizontal={isDesktop}>
              <FormikInput fieldName="email" label="Email" placeholder="Email" autoComplete="email" />
            </ShakeWrap>

            <BtnWrap $horizontal={isDesktop}>
              <GradientButton
                type="button"
                isFullWidth={!isDesktop}
                onClick={async () => {
                  setTouched({ firstName: true, email: true });
                  const errors = await validateForm();
                  if (Object.keys(errors).length > 0) {
                    triggerShake(Object.keys(errors));
                  } else {
                    handleSubmit();
                  }
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <EnvIcon />
                  Subscribe
                </span>
              </GradientButton>
            </BtnWrap>
          </Row>
        </form>
      )}
    </Formik>
  );
};

export default Form;
